import React from 'react'
import PropTypes from 'prop-types'
import { Form } from 'grommet'
import {
  CloseButton,
  ContainedButton,
  HeartsWithStars,
} from 'secondstep-components'

import { getSchoolOption } from 'utils/product/helpers'
import {
  CancelButton,
  ModalHeaderInput,
  ModalWrapperInput,
  TierLabelWrapper,
} from '../../component.styles'

import {
  ButtonsWrapperCreate,
  InputTextField,
  SelectField,
  SectionContainer,
  Row,
  Column,
  Text,
  Link,
  WarningNotice,
  WarningText,
  WarningIcon,
} from './component.styles'
import InformationIcon from './information_icon.svg'
import {
  getCreateConstants,
  CLASS_CONSTANTS,
  SMALL_GROUP_CONSTANTS,
} from './constants'
import TierLabel from 'src/components/TierLabel'

const Create = ({
  courseInstanceName,
  courses,
  dataTestId,
  handleCancel,
  handleClassNameChange,
  handleGradeChange,
  handleSiteChange,
  handleSubmit,
  inputErrors,
  sites,
  lessonsLink,
  selectedCourse,
  selectedSiteId,
  selectedSchool,
  currentSite,
  submitDisabled,
  componentType,
  siteDisabled,
}) => {
  const {
    BUTTON_CANCEL,
    BUTTON_SUBMIT,
    COURSE_INSTANCE_LABEL,
    COURSE_INSTANCE_PLACEHOLDER,
    CREATE_HEADER,
    SCHOOL_LABEL,
    SCHOOL_PLACEHOLDER,
    SELECTED_COURSE_LABEL,
    SELECTED_COURSE_PLACEHOLDER,
    NO_GRADES_ENABLED_MESSAGE,
    ASK_NO_TEACHING,
    PREVIEW_LESSONS,
    ALMOST_THERE,
    START_TEACHING,
    COMPONENT_TYPE_SMALL_GROUP,
    TIER_LABEL_TEXT,
  } = getCreateConstants(componentType)
  let siteValueJsx
  if (selectedSchool) {
    // If selectedSchool is provided, use its text value which should match the options
    siteValueJsx = selectedSchool.text
  } else if (selectedSiteId) {
    // Otherwise, find by selectedSiteId
    const actualSite = sites.find(({ siteId }) => siteId === selectedSiteId)
    const siteOption = actualSite ? getSchoolOption(actualSite) : {}
    siteValueJsx = siteOption.text
  }

  const siteOptions = sites.map(site => getSchoolOption(site))
  const siteTextOptions = siteOptions.map(site => site.text)

  let selectedGrade = selectedCourse
  if (courses.length === 1) {
    selectedGrade = courses[0]
  }

  const siteMismatch =
    Boolean(selectedSchool?.siteId) &&
    Boolean(currentSite?.siteId) &&
    selectedSchool.siteId !== currentSite.siteId

  const hasSelectedSchool = Boolean(selectedSchool?.siteId)
  const hasNoGradeOptions = hasSelectedSchool && courses.length === 0

  return (
    <ModalWrapperInput dataTestId={dataTestId}>
      <ModalHeaderInput dataTestId="header-modal-create">
        {CREATE_HEADER}
        {componentType === COMPONENT_TYPE_SMALL_GROUP && (
          <TierLabelWrapper>
            <TierLabel labelText={TIER_LABEL_TEXT} />
          </TierLabelWrapper>
        )}
      </ModalHeaderInput>
      <SectionContainer>
        <Row>
          <Column>
            <HeartsWithStars />
          </Column>

          <Column>
            <Row>
              <Text>
                <b>{ALMOST_THERE}</b> {START_TEACHING}
              </Text>
            </Row>
            <Row>
              <Text as="span">
                <b>{ASK_NO_TEACHING}</b>
                <Link href={lessonsLink}>{PREVIEW_LESSONS}</Link>
              </Text>
            </Row>
          </Column>
        </Row>
      </SectionContainer>
      <Form aria-label="form" onSubmit={handleSubmit}>
        <SelectField
          autoComplete={false}
          dataTestId="select-site"
          disabled={siteDisabled}
          label={SCHOOL_LABEL}
          name="selectedSite"
          onChange={handleSiteChange}
          options={siteTextOptions}
          placeholder={SCHOOL_PLACEHOLDER}
          value={siteValueJsx}
        />

        <SelectField
          autoComplete={false}
          dataTestId="select-grade"
          disabled={hasNoGradeOptions}
          label={SELECTED_COURSE_LABEL}
          name="selectedCourse"
          onChange={handleGradeChange}
          options={courses}
          placeholder={SELECTED_COURSE_PLACEHOLDER}
          value={selectedGrade}
        />
        {hasNoGradeOptions && (
          <WarningNotice>
            <WarningText as="p" data-testid="no-grades-warning" role="alert">
              <WarningIcon alt="" aria-hidden="true" src={InformationIcon} />
              {NO_GRADES_ENABLED_MESSAGE}
            </WarningText>
          </WarningNotice>
        )}
        <InputTextField
          autoComplete={false}
          dataTestId="create-class-name"
          inputErrors={inputErrors.className}
          label={COURSE_INSTANCE_LABEL}
          maxLength={21}
          name="courseInstanceName"
          onChange={handleClassNameChange}
          placeholder={COURSE_INSTANCE_PLACEHOLDER}
          value={courseInstanceName}
        />
        {siteMismatch && (
          <WarningNotice>
            <WarningText as="p" data-testid="site-switch-warning" role="alert">
              <WarningIcon alt="" aria-hidden="true" src={InformationIcon} />
              {componentType === COMPONENT_TYPE_SMALL_GROUP
                ? 'When you create this small group, your school will change to the one you selected.'
                : 'When you create this course, your school will change to the one you selected.'}
            </WarningText>
          </WarningNotice>
        )}
        <ButtonsWrapperCreate right>
          <CancelButton
            dataTestId={'button-cancel'}
            onClick={handleCancel}
            type="button"
          >
            {BUTTON_CANCEL}
          </CancelButton>
          <ContainedButton
            dataTestId={'button-submit'}
            disabled={submitDisabled}
            type="submit"
          >
            {BUTTON_SUBMIT}
          </ContainedButton>
        </ButtonsWrapperCreate>
        <CloseButton
          dataTestId="button-close"
          onClick={handleCancel}
          type="button"
        />
      </Form>
    </ModalWrapperInput>
  )
}

Create.propTypes = {
  componentType: PropTypes.oneOf([
    CLASS_CONSTANTS.COMPONENT_TYPE_COURSE,
    SMALL_GROUP_CONSTANTS.COMPONENT_TYPE_SMALL_GROUP,
  ]),
  courseInstanceName: PropTypes.string,
  courses: PropTypes.array,
  currentSite: PropTypes.object,
  dataTestId: PropTypes.string,
  handleCancel: PropTypes.func,
  handleClassNameChange: PropTypes.func,
  handleGradeChange: PropTypes.func,
  handleSiteChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  inputErrors: PropTypes.object,
  lessonsLink: PropTypes.string,
  selectedCourse: PropTypes.string,
  selectedSchool: PropTypes.object,
  selectedSiteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteDisabled: PropTypes.bool,
  sites: PropTypes.array,
  submitDisabled: PropTypes.bool,
}

Create.defaultProps = {
  courses: [],
  handleCancel: () => {},
  handleClassNameChange: () => {},
  handleGradeChange: () => {},
  handleSiteChange: () => {},
  sites: [],
  componentType: CLASS_CONSTANTS.COMPONENT_TYPE_COURSE,
}
export default Create
